import styled, { useTheme } from "styled-components";
import {
  Button,
  Container,
  Flex,
  Box,
  Text,
} from "@chakra-ui/react";
import { Settings } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { Title, BigDate } from "../../components/Styled";
import {
  Calendar,
  ScheduleList,
  BuyCoffeeButton,
  Copyright,
  Sun,
  SelectAddressForm,
  WidgetsSection,
} from "../../components";
import { useSchedule } from "../../context/ScheduleProvider";
import Snowfall from "react-snowfall";
import { If, Then, Else } from "react-if";
import { useToday } from "../../hooks/useToday";
import { getMonthImage } from "../../helpers/helpers";
import { motion, AnimatePresence } from "framer-motion";
import React, { useState, useEffect } from 'react';
import { AppTheme } from "../../theme";
import { THEME } from "../../helpers/constans";

// Helper Components
const SettingsButton = ({ theme }: { theme: AppTheme }) => {
  const navigate = useNavigate();
  
  return (
    <Button
      size="sm"
      color="white"
      onClick={() => navigate("/ustawienia")}
      backgroundColor="rgba(255,255,255,0.15)"
      backdropFilter="blur(8px)"
      borderRadius={THEME.borderRadiusFull}
      border="1px solid rgba(255,255,255,0.2)"
      px={4}
      fontFamily="'Plus Jakarta Sans', sans-serif"
      fontWeight={600}
      fontSize="xs"
      letterSpacing="0.02em"
      leftIcon={<Settings size={14} />}
      _hover={{
        backgroundColor: "rgba(255,255,255,0.25)",
        transform: "translateY(-2px)",
      }}
      _active={{
        transform: "translateY(0)",
      }}
      transition="all 0.2s ease"
    >
      Ustawienia
    </Button>
  );
};

const MainContainer = ({ children, background, theme }: { children: React.ReactNode; background: string; theme: AppTheme }) => (
  <Container
    overflow="visible"
    position="relative"
    height="14rem"
    backgroundImage={background}
    backgroundSize="cover"
    backgroundPosition="center"
    _before={{
      content: '""',
      position: "absolute",
      inset: 0,
      background: "linear-gradient(to bottom, rgba(0,0,0,0.45) 0%, rgba(0,0,0,0.2) 40%, rgba(0,0,0,0) 100%)",
      zIndex: 0,
    }}
  >
    <Box position="relative" zIndex={1}>
      {children}
    </Box>
  </Container>
);

// New modern animated loader component
const AnimatedLoaderScreen = ({ theme }: { theme: AppTheme }) => {
  const messages = [
    'Wystawianie koszy...',
    'Liczenie worków...',
    'Gniecenie kartonów...',
    'Zgniatanie puszek...',
    'Segregowanie szkła...',
    'Wiązanie worków...',
    'Ładowanie śmieciarki...'
  ];
  
  const [currentMessageIndex, setCurrentMessageIndex] = useState(0);
  const [progress, setProgress] = useState(0);
  
  // Cycle through messages
  useEffect(() => {
    const messageInterval = setInterval(() => {
      setCurrentMessageIndex((prevIndex) => 
        prevIndex < messages.length - 1 ? prevIndex + 1 : prevIndex
      );
    }, 1000);
    
    return () => clearInterval(messageInterval);
  }, [messages.length]);
  
  // Update progress bar
  useEffect(() => {
    const progressInterval = setInterval(() => {
      setProgress((prevProgress) => {
        // Calculate target progress based on current message
        const targetProgress = ((currentMessageIndex + 1) / messages.length) * 100;
        
        // Smoothly animate to target
        const step = (targetProgress - prevProgress) / 10;
        const newProgress = prevProgress + step;
        
        return newProgress > 100 ? 100 : newProgress;
      });
    }, 100);
    
    return () => clearInterval(progressInterval);
  }, [currentMessageIndex, messages.length]);
  
  return (
    <Container
      minHeight="calc(100vh - 220px)"
      overflow="hidden"
      backgroundColor={theme.background}
      display="flex"
      flex={1}
      padding="20px 1rem 0px 1rem"
      flexDirection="column"
      justifyContent="space-between"
    >
      <Box 
        width="100%" 
        maxWidth="500px" 
        margin="80px auto 0"
        padding="2rem"
        borderRadius={THEME.borderRadiusXl}
        bg={theme.backgroundCard}
        boxShadow={theme.shadowLg}
        border={`1px solid ${theme.border}`}
        position="relative"
        overflow="hidden"
      >
        {/* Decorative gradient blobs */}
        <Box 
          position="absolute" 
          top="-30px" 
          right="-30px" 
          width="180px" 
          height="180px" 
          borderRadius="full" 
          bg={`${theme.primary}15`}
          filter="blur(40px)" 
          zIndex="0"
        />
        <Box 
          position="absolute" 
          bottom="-40px" 
          left="-40px" 
          width="180px" 
          height="180px" 
          borderRadius="full" 
          bg={`${theme.brand}10`}
          filter="blur(40px)" 
          zIndex="0"
        />
        
        {/* Truck icon with animation */}
        <Flex justify="center" mb={6} position="relative" zIndex="1">
          <Box 
            width="80px" 
            height="80px" 
            borderRadius="full" 
            background={theme.gradientPrimary}
            display="flex" 
            alignItems="center" 
            justifyContent="center"
            boxShadow={`0 8px 30px ${theme.primary}40`}
            position="relative"
          >
            <Box
              as={motion.div}
              position="absolute"
              inset="-4px"
              borderRadius="full"
              border={`2px solid ${theme.primary}30`}
              animate={{ 
                scale: [1, 1.15, 1],
                opacity: [0.5, 0.8, 0.5]
              }}
              transition={{
                duration: '2s',
                ease: 'easeInOut',
                repeat: 'Infinity'
              }}
            />
            <Box as="svg" width="36px" height="36px" viewBox="0 0 24 24" fill="none" color="white">
              <path d="M3 6H21V16H3V6Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              <path d="M7 20C8.10457 20 9 19.1046 9 18C9 16.8954 8.10457 16 7 16C5.89543 16 5 16.8954 5 18C5 19.1046 5.89543 20 7 20Z" fill="currentColor"/>
              <path d="M17 20C18.1046 20 19 19.1046 19 18C19 16.8954 18.1046 16 17 16C15.8954 16 15 16.8954 15 18C15 19.1046 15.8954 20 17 20Z" fill="currentColor"/>
            </Box>
          </Box>
        </Flex>
        
        {/* Message display with animation */}
        <Box height="40px" mb={5} textAlign="center" position="relative" zIndex="1">
          <AnimatePresence>
            <Box
              as={motion.div}
              key={currentMessageIndex}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              transition={{ duration: '0.5s' }}
              fontSize="lg"
              fontWeight="600"
              fontFamily="'Outfit', sans-serif"
              color={theme.text}
            >
              {messages[currentMessageIndex]}
            </Box>
          </AnimatePresence>
        </Box>
        
        {/* Progress bar */}
        <Box width="100%" mb={3} position="relative" zIndex="1">
          <Box 
            width="100%" 
            height="10px" 
            borderRadius="full" 
            bg={theme.backgroundMuted}
            overflow="hidden"
          >
            <Box
              as={motion.div}
              height="100%"
              borderRadius="full"
              background={theme.gradientPrimary}
              initial={{ width: 0 }}
              animate={{ width: `${progress}%` }}
              transition={{ duration: '0.3s' }}
            />
          </Box>
        </Box>
        
        {/* Progress information */}
        <Flex justifyContent="space-between" alignItems="center" mt={2} position="relative" zIndex="1">
          <Text fontSize="sm" color={theme.textMuted} fontWeight="600">{Math.round(progress)}%</Text>
        </Flex>
        
        {/* Animated dots */}
        <Flex justify="center" mt={6} marginBottom={2} position="relative" zIndex="1">
          {[0, 1, 2].map((dot) => (
            <Box
              key={dot}
              as={motion.div}
              width="10px"
              height="10px"
              borderRadius="full"
              bg={theme.primary}
              marginX="4px"
              animate={{
                scale: [1, 1.4, 1],
                opacity: [0.4, 1, 0.4]
              }}
              transition={{
                duration: '1.2s',
                repeat: 'Infinity', 
                delay: `${dot * 0.2}s`
              }}
            />
          ))}
        </Flex>
      </Box>
      <Copyright />
    </Container>
  );
};

const ErrorScreen = ({ theme }: { theme: AppTheme }) => (
  <>
    <ErrorTemplate $theme={theme}>
      Wystąpił błąd po stronie dostawcy danych, Przepraszamy!
    </ErrorTemplate>
    <Copyright />
  </>
);

const ScheduleContainer = ({ schedule, today, theme }: { schedule: any; today: any; theme: AppTheme }) => (
  <Container
    overflow="hidden"
    backgroundColor={theme.background}
    display="flex"
    flex={1}
    flexDirection="column"
    px={4}
  >
    {schedule.length > 0 ? (
      <>
        <WidgetsSection scheduleLength={schedule.length} />
        <ScheduleList schedule={schedule} today={today} />
        <Copyright />
      </>
    ) : (
      <Box
        minHeight="60vh"
        padding="3rem 0"
      >
        <SelectAddressForm />
      </Box>
    )}
  </Container>
);

export function Home() {
  const { today, dayMonth, isDecember, isOctober, monthNumber } = useToday();
  const { isLoading, fullAddress, schedule, error } = useSchedule();
  const theme = useTheme() as AppTheme;

  const isSnowVisible = isDecember || isOctober;

  const background = `
    url(${getMonthImage(monthNumber)}), 
    ${theme.gradientHeader}
  `;

  return (
    <>
      {isSnowVisible && <Snowfall style={{ zIndex: 1 }} />}
      <MainContainer background={background} theme={theme}>
        <Flex pt={4} alignItems="flex-start" justifyContent="space-between">
          <Flex flexDirection="column">
            <Title>{fullAddress}</Title>
            <BigDate>{dayMonth}</BigDate>
            <Sun />
          </Flex>
          <Flex alignItems="center" justifyContent="flex-end" flexWrap="wrap" gap={2}>
            <BuyCoffeeButton />
            <SettingsButton theme={theme} />
          </Flex>
        </Flex>
        <Calendar schedule={schedule} today={today} />
      </MainContainer>

      <If condition={isLoading}>
        <Then>
          <If condition={!error}>
            <Then>
              <AnimatedLoaderScreen theme={theme} />
            </Then>
            <Else>
              <ErrorScreen theme={theme} />
            </Else>
          </If>
        </Then>
        <Else>
          <ScheduleContainer schedule={schedule} today={today} theme={theme} />
        </Else>
      </If>
    </>
  );
}

const ErrorTemplate = styled.div<{ $theme: AppTheme }>`
  display: flex;
  justify-content: center;
  padding: 4rem 2rem;
  font-size: 16px;
  font-weight: 500;
  color: ${({ $theme }) => $theme.red};
  text-align: center;
  font-family: 'Plus Jakarta Sans', sans-serif;
`;
